import React, { useEffect, useState } from 'react';
import { auth, db } from '../firebase'; // Make sure to adjust the import paths
import { collection, getDocs, doc, getDoc, query, orderBy, limit, startAfter, getCountFromServer, where } from 'firebase/firestore';
import "../style/Transaction.css";
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';

const TransactionsPage = () => {
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [downloadingCSV, setDownloadingCSV] = useState(false);
    const [error, setError] = useState(null);
    const [walletBalance, setWalletBalance] = useState(0);
    const [lastDoc, setLastDoc] = useState(null); // For pagination
    const [hasMore, setHasMore] = useState(true); // Track if more transactions exist
    const [totalTransactionCount, setTotalTransactionCount] = useState(0); // Total count from server
    const [totalDepositCount, setTotalDepositCount] = useState(0); // Total deposit count from server
    const [cachedAllTransactions, setCachedAllTransactions] = useState(null); // Cache for all transactions
    
    const TRANSACTIONS_PER_PAGE = 15; // Adjust this number based on your needs
    const user = auth.currentUser;

    useEffect(() => {
        if (user) {
            fetchInitialTransactions();
            fetchWalletBalance();
            fetchTotalTransactionCount(); // Get total count separately
            fetchTotalDepositCount(); // Get total deposit count separately
        } else {
            setError('User not logged in. Please log in.');
            setLoading(false);
        }
    }, [user]);

    // Function to fetch total transaction count (uses count aggregation - minimal cost)
    const fetchTotalTransactionCount = async () => {
        try {
            const transactionsRef = collection(db, 'users', user.uid, 'wallet', 'walletDetails', 'transactions');
            const snapshot = await getCountFromServer(transactionsRef);
            setTotalTransactionCount(snapshot.data().count);
        } catch (error) {
            console.error('Error fetching transaction count:', error.message);
            // Fallback: if count aggregation fails, we'll show loaded count
            setTotalTransactionCount(0);
        }
    };

    // Function to fetch total deposit count (uses count aggregation with where clause - minimal cost)
    const fetchTotalDepositCount = async () => {
        try {
            const transactionsRef = collection(db, 'users', user.uid, 'wallet', 'walletDetails', 'transactions');
            const depositQuery = query(transactionsRef, where('type', '==', 'deposit'));
            const snapshot = await getCountFromServer(depositQuery);
            setTotalDepositCount(snapshot.data().count);
        } catch (error) {
            console.error('Error fetching deposit count:', error.message);
            // Fallback: if count aggregation fails, we'll show loaded count
            setTotalDepositCount(0);
        }
    };

    // Function to fetch wallet balance
    const fetchWalletBalance = async () => {
        try {
            const walletRef = doc(db, 'users', user.uid, 'wallet', 'walletDetails');
            const walletSnap = await getDoc(walletRef);

            if (walletSnap.exists()) {
                setWalletBalance(walletSnap.data().balance);
            } else {
                setError('Failed to fetch wallet balance.');
            }
        } catch (error) {
            console.error('Error fetching wallet balance:', error.message);
            setError('Failed to fetch wallet balance.');
        }
    };

    // Function to fetch initial transactions (first page)
    const fetchInitialTransactions = async () => {
        setLoading(true);
        try {
            const transactionsRef = collection(db, 'users', user.uid, 'wallet', 'walletDetails', 'transactions');
            const q = query(
                transactionsRef, 
                orderBy('timestamp', 'desc'),
                limit(TRANSACTIONS_PER_PAGE)
            );
            const querySnapshot = await getDocs(q);
            const fetchedTransactions = querySnapshot.docs.map((doc) => ({
                id: doc.id,
                ...doc.data(),
            }));
            
            setTransactions(fetchedTransactions);
            
            // Set last document for pagination
            if (querySnapshot.docs.length > 0) {
                setLastDoc(querySnapshot.docs[querySnapshot.docs.length - 1]);
            }
            
            // Check if there might be more transactions
            setHasMore(querySnapshot.docs.length === TRANSACTIONS_PER_PAGE);
            
        } catch (error) {
            console.error('Error fetching transactions:', error.message);
            setError('Failed to fetch transactions. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    // Function to load more transactions
    const loadMoreTransactions = async () => {
        if (!lastDoc || !hasMore) return;
        
        setLoadingMore(true);
        try {
            const transactionsRef = collection(db, 'users', user.uid, 'wallet', 'walletDetails', 'transactions');
            const q = query(
                transactionsRef,
                orderBy('timestamp', 'desc'),
                startAfter(lastDoc),
                limit(TRANSACTIONS_PER_PAGE)
            );
            const querySnapshot = await getDocs(q);
            const newTransactions = querySnapshot.docs.map((doc) => ({
                id: doc.id,
                ...doc.data(),
            }));
            
            // Append new transactions to existing ones
            setTransactions(prevTransactions => [...prevTransactions, ...newTransactions]);
            
            // Update last document for next pagination
            if (querySnapshot.docs.length > 0) {
                setLastDoc(querySnapshot.docs[querySnapshot.docs.length - 1]);
            }
            
            // Check if there are more transactions to load
            setHasMore(querySnapshot.docs.length === TRANSACTIONS_PER_PAGE);
            
        } catch (error) {
            console.error('Error loading more transactions:', error.message);
            setError('Failed to load more transactions.');
        } finally {
            setLoadingMore(false);
        }
    };

    // Function to fetch ALL transactions for CSV download
    const fetchAllTransactions = async () => {
        // Check if we already have cached all transactions
        if (cachedAllTransactions) {
            return cachedAllTransactions;
        }

        try {
            const transactionsRef = collection(db, 'users', user.uid, 'wallet', 'walletDetails', 'transactions');
            const q = query(transactionsRef, orderBy('timestamp', 'desc'));
            const querySnapshot = await getDocs(q);
            const allTransactions = querySnapshot.docs.map((doc) => ({
                id: doc.id,
                ...doc.data(),
            }));
            
            // Cache the results to avoid repeated fetches
            setCachedAllTransactions(allTransactions);
            return allTransactions;
        } catch (error) {
            console.error('Error fetching all transactions:', error.message);
            throw error;
        }
    };

    // Function to get transaction type count from currently loaded transactions
    const getTransactionTypeCount = (type) => {
        return transactions.filter(transaction => transaction.type === type).length;
    };

    // Function to get transaction type count from all transactions (for analytics)
    const getAllTransactionTypeCount = (type, allTransactions) => {
        return allTransactions.filter(transaction => transaction.type === type).length;
    };

    // Function to download the transaction data as CSV
    const downloadCSV = async () => {
        setDownloadingCSV(true);
        try {
            // Fetch all transactions for CSV download
            const allTransactions = await fetchAllTransactions();
            
            const csvRows = [
                ["Date", "Type", "Amount (₹)", "Balance Before (₹)", "Balance After (₹)"], // CSV headers
                ...allTransactions.map(transaction => [
                    transaction.timestamp ? new Date(transaction.timestamp.toDate()).toLocaleString() : 'NA',
                    transaction.type || 'NA',
                    transaction.amount !== undefined && transaction.amount !== null ? transaction.amount : 'NA',
                    transaction.balanceBefore !== undefined && transaction.balanceBefore !== null ? transaction.balanceBefore : 'NA',
                    transaction.balanceAfter !== undefined && transaction.balanceAfter !== null ? transaction.balanceAfter : 'NA'
                ])
            ];

            const csvContent = csvRows.map(e => e.join(",")).join("\n");
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });

            const link = document.createElement("a");
            const url = URL.createObjectURL(blob);
            link.setAttribute("href", url);
            link.setAttribute("download", `transaction_history_complete_${new Date().toISOString().split('T')[0]}.csv`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            
        } catch (error) {
            console.error('Error downloading CSV:', error.message);
            setError('Failed to download CSV. Please try again.');
        } finally {
            setDownloadingCSV(false);
        }
    };

    // Function to clear cache (optional - for refresh)
    const clearCache = () => {
        setCachedAllTransactions(null);
    };

    return (
        <div className="transactions-page">
            <Header />
            <div className="transactions-container">
                <Sidebar />
                <div className="main-content">
                    <h1 className="page-title">Transaction History</h1>
                    {error && <p className="error-message">{error}</p>}
                    {loading ? (
                        <p className="loading-message">Loading transactions...</p>
                    ) : transactions.length === 0 ? (
                        <p className="loading-message">No transactions found.</p>
                    ) : (
                        <>
                            <div className="analytics-section">
                                <div className="analytics-card">
                                    <h3>Wallet Balance</h3>
                                    <p>₹{walletBalance.toFixed(2)}</p>
                                </div>
                                <div className="analytics-card">
                                    <h3>Total Transactions</h3>
                                    <p>{totalTransactionCount}</p>
                                    {/* <small>({transactions.length} loaded)</small> */}
                                </div>
                                <div className="analytics-card">
                                    <h3>Total Deposits</h3>
                                    <p>{totalDepositCount}</p>
                                    {/* <small>({getTransactionTypeCount('deposit')} loaded)</small> */}
                                </div>
                                {/* <div className="analytics-card">
                                    <h3>Withdrawals (Loaded)</h3>
                                    <p>{getTransactionTypeCount('withdrawal')}</p>
                                </div> */}
                            </div>
                            
                            <div className="action-buttons">
                                <button 
                                    onClick={downloadCSV} 
                                    className="download-csv-button"
                                    disabled={downloadingCSV}
                                >
                                    {downloadingCSV ? 'Downloading All Transactions...' : 'Download CSV'}
                                </button>
                                
                                {cachedAllTransactions && (
                                    <button 
                                        onClick={clearCache} 
                                        className="clear-cache-button"
                                        title="Clear cached data and refresh"
                                    >
                                        Clear Cache
                                    </button>
                                )}
                            </div>
                            
                            <table className="transactions-table">
                                <thead>
                                    <tr>
                                        <th>Date</th>
                                        <th>Type</th>
                                        <th>Amount (₹)</th>
                                        <th>Balance Before (₹)</th>
                                        <th>Balance After (₹)</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {transactions.map((transaction) => (
                                        <tr key={transaction.id}>
                                            <td>{transaction.timestamp ? new Date(transaction.timestamp.toDate()).toLocaleString() : 'NA'}</td>
                                            <td>{transaction.type ? transaction.type : 'NA'}</td>
                                            <td>{transaction.amount !== undefined && transaction.amount !== null ? transaction.amount.toFixed(2) : 'NA'}</td>
                                            <td>{transaction.balanceBefore !== undefined && transaction.balanceBefore !== null ? transaction.balanceBefore.toFixed(2) : 'NA'}</td>
                                            <td>{transaction.balanceAfter !== undefined && transaction.balanceAfter !== null ? transaction.balanceAfter.toFixed(2) : 'NA'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            
                            {/* Load More Button */}
                            {hasMore && (
                                <div className="load-more-section">
                                    <button 
                                        onClick={loadMoreTransactions} 
                                        className="load-more-button"
                                        disabled={loadingMore}
                                    >
                                        {loadingMore ? 'Loading More...' : 'Read More'}
                                    </button>
                                </div>
                            )}
                            
                            {!hasMore && transactions.length > 0 && (
                                <p className="end-message">All transactions loaded.</p>
                            )}
                            
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default TransactionsPage;